//Library imports
import React, { useState, useEffect, lazy, Suspense } from "react";
import { Col, Row, Skeleton } from "antd";
import { useSelector } from "react-redux";
import store, { RootState } from "store";
import styled from "styled-components";

//Local imports
import ButtonRightIconGreen from "../../../Common/Components/ButtonRightIconGreen";
import ButtonRightIcon from "../../../Common/Components/ButtonRightIcon";
import PeopleListHolder from "./PeopleListHolder";
import CreateContactSidebar from "./CreateContactSidebar";
import CreateNewList from "./CreateNewList";
import EditList from "./EditList";
import Add from "../../../../assets/img/icons/addWhite.svg";
import Import from "../../../../assets/img/icons/Import.svg";
import Send from "../../../../assets/img/icons/Send.svg";
import {
  openDetailProfile,
  openCreateNewContactSidebar,
  GLOBAL_ACTION,
  closeOverlay,
  closeDetailProfile,
} from "modules/GlobalActions";
import { changeResetCount, exportListDetails } from "../Lists/actions";
import { PEOPLE_AUDIENCE } from "constants/actionTypes";
import AudienceSearchTab from "../Components/AudienceSearchTab";
import { getDesignations, getOrganizations } from "../actions";
import { getQueryParam, removeGillRoyIcon } from "utils";
const ImportListFlow = lazy(() => import("../Import/importListFlow"));
const ExportPopup = lazy(() => import("../Components/ExportPopup"));
const FilterDropdownStatus = lazy(() => import("./FilterDropdownStatus"));
const FilterDropdownDesignation = lazy(() => import("./FilterDropdownDesignation"));
const FilterDropdownOrganization = lazy(() => import("./FilterDropdownOrganization"));
const FilterDropdownPersonality = lazy(() => import("./FilterDropdownPersonality"));
const FloatingSidebarTwo = lazy(() => import("modules/Common/Components/FloatingSidebarTwo"));
const AllPeopleList = lazy(() => import("./AllPeopleList"));

interface AudiencePeopleProps {
  setTabState: (value: string) => void;
  tabState: string;
  page: string;
}

const SquareSkeleton = () => <Skeleton.Button active={true} size={"default"} shape={"square"} block={false} />;

const AudiencePeople: React.FC<AudiencePeopleProps> = ({ tabState, page }) => {
  //Local States
  const [showImportListAsider, setShowImportListAsider] = useState(false);
  const [showCreateContactFormState, setshowCreateContactFormState] = useState(false);
  const [closeSidebarInstantly, setCloseSidebarInstantly] = useState(true);
  const [firstTimeLoad, setFirstTimeLoad] = useState(true);
  const [updateTotalCount, setUpdateTotalCount] = useState<boolean>(false);
  const [skipInitialMount, setSkipInitialMount] = useState(true);
  const [selectedOrganization, setSelectedOrganization] = useState<string[]>([]); // organization filter state
  const [selectedDesignation, setSelectedDesignation] = useState<string[]>([]); // designation filter state
  const [selectedPersonalityType, setSelectedPersonalityType] = useState<string[]>([]); // personality filter state
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]); // status filter state
  const [clearFiltersCheck, setClearFiltersCheck] = useState(false); // alter this state to trigger get contacts call without any filters
  const [exportContactsFilterParams, setExportContactsFilterParams] = useState({}); // export contacts filter params
  const [disableExportButton, setDisableExportButton] = useState(false); // disable export button, set to true to diable export button

  //Redux States
  const showCreateContactForm = useSelector((state: RootState) => state.audience.showCreateContactForm);
  const searchTermPeople = useSelector((state: RootState) => state.peopleAudience.searchTermPeople);
  const openEditContactSidebar = useSelector((state: RootState) => state.peopleAudience.openEditContactSidebar);
  const totalCount = useSelector((state: RootState) => state.peopleAudience.totalCount);
  const allPeopleloading: boolean = useSelector((state: RootState) => state.peopleAudience.allPeopleloading);
  const allPeople = useSelector((state: RootState) => state.peopleAudience.allPeople);
  const profiledPeopleLoading: boolean = useSelector(
    (state: RootState) => state.peopleAudience.profiledPeopleloading
  );
  const unprofiledPeopleLoading: boolean = useSelector(
    (state: RootState) => state.peopleAudience.unprofiledPeopleloading
  );
  const audienceFiltersApplied: boolean = useSelector(
    (state: RootState) => state.uiReducer.audienceFiltersApplied
  );

  // Component Logic
  useEffect(() => {
    if (
      selectedOrganization.length === 0 &&
      selectedDesignation.length === 0 &&
      selectedPersonalityType.length === 0 &&
      selectedStatus.length === 0 &&
      audienceFiltersApplied
    ) {
      clearFiltersHandler();
    }
  }, [selectedOrganization, selectedDesignation, selectedPersonalityType, selectedStatus]);

  useEffect(() => {
    if (allPeopleloading || profiledPeopleLoading || unprofiledPeopleLoading) {
      setDisableExportButton(true);
    } else {
      setDisableExportButton(false);
    }
  }, [allPeopleloading, profiledPeopleLoading, unprofiledPeopleLoading]);

  const showDetailProfileHandler = () => {
    openDetailProfile();
  };

  const closeSidebarInstantlyHandler = (showCreateContactForm: boolean) => {
    setshowCreateContactFormState(showCreateContactForm);
    closeOverlay();
  };

  useEffect(() => {
    if (!firstTimeLoad) {
      if (!showCreateContactForm) {
        closeSidebarInstantly
          ? closeSidebarInstantlyHandler(showCreateContactForm)
          : setshowCreateContactFormState(showCreateContactForm);
      } else {
        setshowCreateContactFormState(showCreateContactForm);
      }
    } else {
      setFirstTimeLoad(false);
    }
  }, [showCreateContactForm]);

  useEffect(() => {
    getOrganizations();
    getDesignations();
    return () => {
      closeDetailProfile(); // close pserson detail asider on unmount
      changeResetCount();
      clearFiltersHandler();
    };
  }, []);

  const importListHandler = () => {
    store.dispatch({
      type: GLOBAL_ACTION.IMPORT_LIST_POPOVER,
      payload: { importListAsiderState: true },
    });
    setShowImportListAsider(true);
    if (window.location.pathname.startsWith("/audiences")) {
      removeGillRoyIcon();
    }
  };

  useEffect(() => {
    if (skipInitialMount) {
      setSkipInitialMount(false);
    } else {
      resetFilters();
    }
    store.dispatch({
      type: PEOPLE_AUDIENCE.RESET_TOTAL_COUNT,
    });
  }, [tabState]);

  useEffect(() => {
    if (updateTotalCount && !allPeopleloading && !profiledPeopleLoading && !unprofiledPeopleLoading) {
      setUpdateTotalCount(false);
    }
  }, [totalCount]);

  const exportPeopleFiltersUpdate = () => {
    let exportContactsFilterParamsObj: any = {};
    if (selectedOrganization.length > 0) {
      exportContactsFilterParamsObj["filter_by_organization"] = selectedOrganization;
    } else {
      delete exportContactsFilterParamsObj["filter_by_organization"];
    }
    if (selectedDesignation.length > 0) {
      exportContactsFilterParamsObj["filter_by_designation"] = selectedDesignation;
    } else {
      delete exportContactsFilterParamsObj["filter_by_designation"];
    }
    if (selectedPersonalityType.length > 0) {
      exportContactsFilterParamsObj["personality_type"] = selectedPersonalityType;
    } else {
      delete exportContactsFilterParamsObj["personality_type"];
    }
    if (selectedStatus.length > 0) {
      exportContactsFilterParamsObj["status"] = selectedStatus;
    } else {
      delete exportContactsFilterParamsObj["status"];
    }
    setExportContactsFilterParams(exportContactsFilterParamsObj);
    store.dispatch({
      type: PEOPLE_AUDIENCE.EXPORT_FILTER_PEOPLE_AUDIENCE,
      payload: exportContactsFilterParamsObj,
    });
  };

  const exportAudiencePeopleHandler = () => {
    exportListDetails({
      export: true,
      searchTermPeople: searchTermPeople,
      profiled: tabState === "all" ? "-1" : tabState === "profiled" ? "1" : "0",
      ...exportContactsFilterParams,
      ...(getQueryParam("email_id") ? { emailId: getQueryParam("email_id") } : {}),
      ...(getQueryParam("uuid") ? { uuid: getQueryParam("uuid") } : {}),
    });
  };

  const ApplyFiltersHandler = () => {
    if (tabState === "all") {
      store.dispatch({
        type: PEOPLE_AUDIENCE.LISTS_LOAD_ALL_PEOPLE_RESET, // reset all people
      });
    }
    if (tabState === "profiled") {
      store.dispatch({
        type: PEOPLE_AUDIENCE.LISTS_LOAD_PROFILED_RESET, // reset profiled people
      });
    }
    if (tabState === "unprofiled") {
      store.dispatch({
        type: PEOPLE_AUDIENCE.LISTS_LOAD_UNPROFILED_RESET, // reset unprofiled people
      });
    }
    setUpdateTotalCount(true);
    exportPeopleFiltersUpdate();
    store.dispatch({
      type: PEOPLE_AUDIENCE.SET_FILTERS,
      payload: {
        designations: selectedDesignation,
        organizations: selectedOrganization,
        personality: selectedPersonalityType,
        status: selectedStatus,
      },
    });
    store.dispatch({
      type: GLOBAL_ACTION.APPLY_AUDIENCE_FILTERS,
    });
    if (!audienceFiltersApplied) {
      store.dispatch({
        type: GLOBAL_ACTION.AUDIENCE_FILTERS_APPLIED,
        payload: true,
      });
    }
  };

  const resetFilters = () => {
    setSelectedOrganization([]);
    setSelectedDesignation([]);
    setSelectedPersonalityType([]);
    setSelectedStatus([]);
    setUpdateTotalCount(true);
    store.dispatch({
      type: PEOPLE_AUDIENCE.SET_FILTERS,
      payload: {
        designations: [],
        organizations: [],
        personality: [],
        status: [],
        searchTerm: searchTermPeople,
      },
    });
    if (tabState === "all") {
      store.dispatch({
        type: PEOPLE_AUDIENCE.LISTS_LOAD_ALL_PEOPLE_RESET,
      });
    }
    if (tabState === "profiled") {
      store.dispatch({
        type: PEOPLE_AUDIENCE.LISTS_LOAD_PROFILED_RESET,
      });
    }
    if (tabState === "unprofiled") {
      store.dispatch({
        type: PEOPLE_AUDIENCE.LISTS_LOAD_UNPROFILED_RESET,
      });
    }
  };

  const clearFiltersHandler = () => {
    resetFilters();
    exportPeopleFiltersUpdate(); // reset the export filters as well as the apply filters
    setClearFiltersCheck(!clearFiltersCheck);

    if (audienceFiltersApplied) {
      store.dispatch({
        type: GLOBAL_ACTION.AUDIENCE_FILTERS_APPLIED,
        payload: false,
      });
    }
  };

  return (
    <Wrapper className="audience-content-outer">
      <Row>
        <Col span={16} className="audience-people-search">
          <AudienceSearchTab page={page} isProfiled={true} tabState={"all"} />
        </Col>
        <Col className="gutter-row" span={8}>
          <div
            className="d-flex flex-row-reverse ant-space ant-space-horizontal ant-space-align-center"
            style={{ height: "100%" }}
          >
            <ButtonRightIcon
              label="Import a list"
              icon={Import}
              onClick={() => {
                importListHandler();
              }}
            />
            <span onClick={() => openCreateNewContactSidebar()} className="me-3">
              <ButtonRightIconGreen
                label="Create new contact"
                icon={Add}
                classIcon="plus15"
                className="people-green-btn"
                idBtn="people-green-btn-contact"
              />
            </span>
            <span className="me-3">
              <ButtonRightIcon
                label="Export"
                disable={disableExportButton || totalCount === 0}
                onClick={exportAudiencePeopleHandler}
                icon={Send}
                outlineClass="auto-width"
              />
            </span>
          </div>
        </Col>
      </Row>

      <Row gutter={12} className="mt-3 filter-row">
        <Col span={2.5}>
          <Suspense fallback={<SquareSkeleton />}>
            <FilterDropdownOrganization
              selectedOrganization={selectedOrganization}
              setSelectedOrganization={setSelectedOrganization}
              tabState={tabState}
            />
          </Suspense>
        </Col>
        <Col span={2.5}>
          <Suspense fallback={<SquareSkeleton />}>
            <FilterDropdownDesignation
              selectedDesignation={selectedDesignation}
              setSelectedDesignation={setSelectedDesignation}
              tabState={tabState}
            />
          </Suspense>
        </Col>
        <Col className={`${tabState === "unprofiled" ? "disabled-filter" : ""}`} span={2.5}>
          <Suspense fallback={<SquareSkeleton />}>
            <FilterDropdownPersonality
              selectedPersonalityType={selectedPersonalityType}
              setSelectedPersonalityType={setSelectedPersonalityType}
            />
          </Suspense>
        </Col>
        <Col span={2.5}>
          <Suspense fallback={<SquareSkeleton />}>
            <FilterDropdownStatus selectedStatus={selectedStatus} setSelectedStatus={setSelectedStatus} />
          </Suspense>
        </Col>
        <Col span={2.5}>
          <ButtonRightIconGreen
            hidden={
              !(
                selectedOrganization.length > 0 ||
                selectedDesignation.length > 0 ||
                selectedPersonalityType.length > 0 ||
                selectedStatus.length > 0
              )
            }
            onClick={ApplyFiltersHandler}
            label="Apply Filters"
            marginTop="0px"
            noIcon
            className="people-apply-filters"
          />
        </Col>
        <Col
          hidden={
            !(
              selectedOrganization.length > 0 ||
              selectedDesignation.length > 0 ||
              selectedPersonalityType.length > 0 ||
              selectedStatus.length > 0
            )
          }
          span={2.5}
          className="clear-text"
        >
          <span onClick={clearFiltersHandler}>Clear all</span>
        </Col>

        <Col
          span={
            selectedOrganization.length > 0 ||
            selectedDesignation.length > 0 ||
            selectedPersonalityType.length > 0 ||
            selectedStatus.length > 0
              ? 13
              : 16
          }
          className="count-col"
        >
          {updateTotalCount || (allPeopleloading && allPeople.length === 0) ? (
            <div className="d-flex flex-row-reverse count">{"Fetching Count..."}</div>
          ) : (
            <div className="d-flex flex-row-reverse count">{`${totalCount} Total`}</div>
          )}
        </Col>
      </Row>

      <Col className="gutter-row mt-2 audience-content-inner" span="24">
        <Suspense fallback={<PeopleListHolder loading={true} />}>
          <AllPeopleList
            tabState={tabState}
            showDetailProfileHandler={showDetailProfileHandler}
            clearFilterCheck={clearFiltersCheck}
            exportPeopleFiltersUpdate={exportPeopleFiltersUpdate}
          />
        </Suspense>
      </Col>

      <Suspense fallback={<></>}>
        <FloatingSidebarTwo isToggle={showCreateContactFormState} className="add-new-contact">
          <CreateContactSidebar selectedTab={tabState} setCloseSidebarInstantly={setCloseSidebarInstantly} />
        </FloatingSidebarTwo>
        <FloatingSidebarTwo isToggle={openEditContactSidebar}>
          <CreateContactSidebar
            detailProfileAsider={true}
            editSidebar={true}
            setCloseSidebarInstantly={setCloseSidebarInstantly}
          />
        </FloatingSidebarTwo>
        <FloatingSidebarTwo isToggle={false}>
          <CreateNewList />
        </FloatingSidebarTwo>
        <FloatingSidebarTwo isToggle={false}>
          <EditList />
        </FloatingSidebarTwo>
        {/* <DeleteSelected /> */}
        <ImportListFlow
          showImportListAsider={showImportListAsider}
          setShowImportListAsider={setShowImportListAsider}
        />
        <ExportPopup tabState={tabState} />
      </Suspense>
    </Wrapper>
  );
};

export default AudiencePeople;

const Wrapper = styled.div`
  margin-bottom: 0px;
  /* margin-top: 23px; */
  .filter-row {
    position: relative;
    .count-col {
      position: absolute;
      right: 0;
      margin-top: 5px;
      /* @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        margin-top: 0px;
      } */
    }
  }
  .clear-text {
    color: #7e7e7e;
    font-family: "Roboto";
    font-size: 14px;
    font-weight: 500;
    display: flex;
    align-items: center;
    cursor: pointer;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 12px;
    }
  }
  .disabled-filter {
    pointer-events: none;
    opacity: 0.5;
  }
  .count {
    height: 100%;
    align-items: center;
    color: #7e7e7e;
    font-family: "Roboto Slab", serif;
    font-size: 16px;
    font-weight: 500;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 14px;
    }
  }
  .filters-tabs {
    ul {
      list-style-type: none;
      padding: 0;
      margin: 0;
      display: flex;
      li {
        margin: 0px;
        padding: 0;
        button {
          .wrapper {
            word-break: keep-all;
            white-space: nowrap;
          }
        }
        &:nth-child(1) {
          button {
            border-radius: 8px 0px 0px 8px;
            .wrapper {
              border-radius: 8px 0px 0px 8px;
            }
          }
        }
        &:nth-child(2) {
          button {
            border-radius: 0;
            .wrapper {
              border-radius: 0;
            }
          }
        }
        &:nth-child(3) {
          button {
            border-radius: 0 8px 8px 0;
            .wrapper {
              border-radius: 0 8px 8px 0;
            }
          }
        }
      }
    }
  }
  .add-new-contact {
    .footer-sider {
      @media (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        width: 330px;
      }
    }
  }
  .people-green-btn {
    white-space: nowrap;
    @media all and (max-width: 1440px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      height: 31px;
    }
  }
  .audience-people-search {
    .search-wraper {
      .search-bar {
        width: calc(100% - 25px);
        @media all and (max-width: 1700px) {
          width: calc(100% - 50px);
        }
        @media all and (max-width: 1650px) {
          width: calc(100% - 75px);
        }
        @media all and (max-width: 1550px) {
          width: calc(100% - 120px);
        }
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          width: calc(100% - 100px);
        }
        @media all and (max-width: 1260px) {
          width: calc(100% - 140px);
        }
      }
    }
  }
  .people-apply-filters {
    padding: 5px 2px 5px 16px;
  }
`;
